import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Alert, CircularProgress, Chip, Stack, Paper, Grid,
  LinearProgress, IconButton, Tooltip, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// 하네스 현황 (Telemetry Status) — REDERI 가 스스로를 측정하는 파이프라인의 요약.
// 데이터: GET /api/telemetry/summary (rederi-telemetry D1, READ-ONLY, 직전 7일).
//   ① 수집 파이프라인이 살아있나  ② 조직이 표준을 실제로 쓰나
//   ③ 하네스가 개선할 여지가 있나(밴드 분포)  ④ 느리거나 비싸지지 않나

// 밴드 표시 순서 + 사람이 읽는 설명.
const BAND_ORDER = ['high', 'medium', 'low', 'no_match', 'command-route'];
const BAND_DESC = {
  high: '확신 라우팅 — 특정 에이전트를 강하게 추천',
  medium: '중간 확신',
  low: '저확신 — 후보는 있으나 약함',
  no_match: '매칭 후보 없음',
  'command-route': '슬래시 커맨드 경로 (매칭 대상 아님)',
};

// 밴드 → 테마 색. 의미색(semantic)만 사용 — 액센트와 분리.
function bandColor(theme, band) {
  const p = theme.palette;
  switch (band) {
    case 'high': return p.success.main;
    case 'medium': return p.warning.main;
    case 'low': return p.primary.main;
    case 'command-route': return p.info.main;
    default: return p.text.secondary; // no_match 등
  }
}

function relTime(ms) {
  if (!ms) return '기록 없음';
  const diff = Date.now() - ms;
  const m = Math.round(diff / 60000);
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}시간 전`;
  return `${Math.round(h / 24)}일 전`;
}

const fmtUTC = (ms) => (ms ? `${new Date(ms).toISOString().slice(0, 16).replace('T', ' ')} UTC` : '—');
const num = (n) => (n ?? 0).toLocaleString();

// 큰 토큰 값 압축 표기(24.5M, 5.5B). 정확 수치는 title 툴팁으로 노출.
const fmtTok = (n) => {
  const v = n ?? 0;
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v}`;
};

// 수집 생존 판정 — 최근 유입 시각 기준.
function ingestStatus(lastMs) {
  if (!lastMs) return { label: '데이터 없음', color: 'error' };
  const h = (Date.now() - lastMs) / 3_600_000;
  if (h < 12) return { label: '수집 정상', color: 'success' };
  if (h < 48) return { label: '수집 지연', color: 'warning' };
  return { label: '수집 중단?', color: 'error' };
}

export default function TelemetryPage() {
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetch('/api/telemetry/summary')
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.detail || body?.reason || body?.error || `HTTP ${r.status}`);
        return body;
      })
      .then((d) => setData(d))
      .catch((e) => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const lastIngest = useMemo(() => {
    if (!data) return null;
    const a = data.ingest?.last_agent_ts ?? 0;
    const d = data.ingest?.last_decision_ts ?? 0;
    return Math.max(a, d) || null;
  }, [data]);

  const status = ingestStatus(lastIngest);

  const bands = useMemo(() => {
    if (!data?.bands) return [];
    const maxN = Math.max(1, ...data.bands.map((b) => b.n));
    return [...data.bands]
      .sort((a, b) => BAND_ORDER.indexOf(a.band) - BAND_ORDER.indexOf(b.band))
      .map((b) => ({ ...b, w: (b.n / maxN) * 100 }));
  }, [data]);

  const maxDaily = Math.max(1, ...(data?.daily ?? []).map((d) => d.decisions));

  // IMP-028/029 실행 방식 무관 per-agent 스코어카드 (인라인 + 스폰 통합).
  const scorecard = data?.agentScorecard ?? [];
  const maxScoreTokens = Math.max(1, ...scorecard.map((a) => a.tokens ?? 0));
  const sv = data?.sessionVolume ?? null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1080, mx: 'auto' }}>
      {/* 헤더 */}
      <Stack direction="row" flexWrap="wrap" alignItems="flex-start" justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            📡 하네스 현황
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '62ch' }}>
            REDERI 가 스스로를 측정하는 파이프라인이 <b>살아있는지</b>, 조직이 이번 주 얼마나 이 도구를 썼는지,
            그리고 <b>하네스가 스스로 개선할 여지</b>가 얼마나 되는지를 한 화면에서 봅니다. (직전 7일)
          </Typography>
        </Box>
        <Stack alignItems="flex-end" gap={0.5}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Chip
              size="small"
              color={status.color}
              variant="outlined"
              icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
              label={status.label}
              sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}
            />
            <Tooltip title="새로고침" arrow>
              <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
            최근 유입 {fmtUTC(lastIngest)} · {relTime(lastIngest)}
          </Typography>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          telemetry 조회 실패: {error}
          <br />
          <Typography variant="caption">
            D1 바인딩(TELEMETRY_DB)이 없거나 계정이 다를 수 있습니다 — wrangler.toml / Cloudflare 계정 확인.
          </Typography>
        </Alert>
      )}

      {data && (
        <Stack gap={2}>
          {/* KPI 스트립 */}
          <Grid container spacing={2}>
            <Kpi title="수집 상태" value={status.label} why="Worker·D1·R2 응답 여부. 파이프라인 생존 신호." accent={status.color} />
            <Kpi title="라우팅 결정" value={`${num(data.decisions?.total)}`} unit="/ 7d" why="프롬프트마다 하네스가 남긴 결정 수 = dogfooding 활동량." />
            <Kpi title="Agent 실행" value={num(data.agent?.calls)} unit="/ 7d" why={`실제 서브에이전트 호출. 성공률 ${data.agent?.ok_rate != null ? Math.round(data.agent.ok_rate * 100) + '%' : '—'}.`} />
            <Kpi title="활성 범위" value={`${num(data.agent?.repos)} repo · ${num(data.agent?.users)} user`} why="몇 개 제품·몇 명이 표준을 실제로 돌리는가." />
          </Grid>

          <Grid container spacing={2}>
            {/* 밴드 분포 */}
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h4" gutterBottom>🎯 하네스 라우팅 밴드 분포</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'primary.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
                  각 프롬프트를 어떤 확신도로 에이전트에 매칭했는가. <b>확신(high) 비율이 낮을수록 매처 키워드를 손봐야 한다는 신호</b>
                  {' '}— 이게 "자기 개선 하네스"의 핵심 계기판.
                </Typography>

                <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                  {bands.map((b) => (
                    <Box key={b.band} sx={{ display: 'grid', gridTemplateColumns: '108px 1fr auto', alignItems: 'center', gap: 1.5, py: 0.75 }}>
                      <Tooltip title={BAND_DESC[b.band] || ''} arrow placement="left">
                        <Stack direction="row" alignItems="center" gap={0.75}>
                          <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: bandColor(theme, b.band), flex: 'none' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{b.band}</Typography>
                        </Stack>
                      </Tooltip>
                      <LinearProgress
                        variant="determinate"
                        value={b.w}
                        sx={{ height: 9, borderRadius: 5, [`& .MuiLinearProgress-bar`]: { bgcolor: bandColor(theme, b.band) }, bgcolor: 'action.hover' }}
                      />
                      <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }}>
                        <b>{num(b.n)}</b> · {b.pct}%
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                <Box sx={{ mt: 2, p: 1.25, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider' }}>
                  <Typography variant="body2">
                    확신 라우팅(high)은 <b style={{ color: theme.palette.success.main }}>{data.decisions?.high_pct}%</b>,
                    {' '}저확신+무매칭(low+no_match)이 <b style={{ color: theme.palette.warning.main }}>{data.decisions?.low_conf_pct}%</b>.
                    {' '}→ 매처 임계치·키워드 튜닝 여지 (Self-Improving Harness 목표).
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* IMP-028/029 실행 방식 무관 per-agent 스코어카드 — 인라인+스폰 통합 */}
          <Grid container spacing={2}>
            {/* 실제 실행된 Agent — transcript 정확 (인라인 + 스폰) */}
            <Grid item xs={12} md={7}>
              <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 0.5 }}>
                  <Typography variant="h4">🤖 실제 실행된 Agent</Typography>
                  <Tooltip title={data.window?.from && data.window?.to ? `${data.window.from.slice(0, 10)} ~ ${data.window.to.slice(0, 10)} (누적 아님, 롤링 창)` : '누적 아님, 롤링 창'} arrow>
                    <Chip label="최근 7일" size="small" variant="outlined" sx={{ height: 20, fontSize: 11, fontWeight: 600, color: 'success.main', borderColor: 'success.main' }} />
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'success.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
                  실행 방식(인라인/스폰) 무관 실제 활동. <b>인라인</b> = 메인 루프가 역할 수행(LLM 분류 귀속),
                  {' '}<b>스폰</b> = 서브에이전트. 토큰은 cache 포함 정확 계측(transcript 원장), <b>토큰 순</b> 정렬.
                  {' '}<b style={{ color: theme.palette.success.main }}>초록</b>은 REDERI 에이전트,
                  {' '}<span style={{ color: theme.palette.text.secondary }}>파랑 · <code>직접</code></span>은 orchestrator/conversation(특정 agent 아닌 직접 엔지니어링·대화),
                  {' '}<span style={{ color: theme.palette.text.disabled }}>회색 · <code>내장</code></span>은 Claude Code 내장(explore·general-purpose 등).
                </Typography>

                {scorecard.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    직전 7일 정확 계측 없음 — transcript-analyzer 가 도는 설치 PC 에서만 누적됩니다.
                  </Typography>
                )}
                <Stack divider={<Divider sx={{ borderStyle: 'dashed' }} />}>
                  {scorecard.map((a) => {
                    const builtin = a.source === 'builtin';
                    const direct = a.source === 'direct';
                    const barColor = builtin ? theme.palette.text.disabled : direct ? theme.palette.info.main : theme.palette.success.main;
                    const nameColor = builtin ? 'text.disabled' : direct ? 'text.secondary' : 'text.primary';
                    return (
                      <Box key={a.agent} sx={{ py: 0.85 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5, gap: 1 }}>
                          <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', color: nameColor }}>{a.agent}</Typography>
                            {builtin && (
                              <Chip label="내장" size="small" variant="outlined"
                                sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.6 }, color: 'text.disabled', borderColor: 'divider' }} />
                            )}
                            {direct && (
                              <Chip label="직접" size="small" variant="outlined"
                                sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.6 }, color: 'info.main', borderColor: 'info.main' }} />
                            )}
                          </Stack>
                          <Tooltip title={`활동 ${num(a.activities)}회 (인라인 ${num(a.inline)} + 스폰 ${num(a.spawn)}) · total ${num(a.tokens)} tokens`} arrow>
                            <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', whiteSpace: 'nowrap' }} color="text.secondary">
                              <b>{num(a.activities)}</b> (인{num(a.inline)}+스{num(a.spawn)}) · <b>{fmtTok(a.tokens)}</b>
                            </Typography>
                          </Tooltip>
                        </Stack>
                        <LinearProgress variant="determinate" value={(a.tokens / maxScoreTokens) * 100} sx={{ height: 6, borderRadius: 3, bgcolor: 'action.hover', [`& .MuiLinearProgress-bar`]: { bgcolor: barColor } }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, display: 'block', mt: 0.35 }}>
                          output {fmtTok(a.out_tokens)} · cache {fmtTok(a.cache_tokens)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            </Grid>

            {/* 세션 볼륨 — 인라인 포함 실사용 */}
            <Grid item xs={12} md={5}>
              <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h4" gutterBottom>🧮 세션 볼륨 — 인라인 포함 실사용</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'success.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
                  <code>session_stats</code> 기반. <b>spawn 무관</b>, 메인 루프가 인라인으로 처리한 작업까지 포함한 <b>실제 사용 규모</b>.
                  {' '}Task 스폰만 세는 위 카드와 자릿수가 다른 게 정상.
                </Typography>

                {!sv ? (
                  <Typography variant="body2" color="text.secondary">
                    직전 7일 세션 계측 없음 — 설치 PC 기준 누적.
                  </Typography>
                ) : (
                  <Grid container spacing={1.5}>
                    <SvTile label="세션" value={num(sv.sessions)} />
                    <SvTile label="사용자" value={num(sv.users)} />
                    <SvTile label="repo" value={num(sv.repos)} />
                    <SvTile label="turns" value={num(sv.turns)} />
                    <SvTile label="output 토큰" value={fmtTok(sv.out_tokens)} title={`${num(sv.out_tokens)} tokens`} accent="success.main" />
                    <SvTile label="cache 토큰" value={fmtTok(sv.cache_tokens)} title={`${num(sv.cache_tokens)} tokens`} />
                  </Grid>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* 일자별 추이 */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="h4" gutterBottom>📈 일자별 라우팅 결정 추이</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', borderLeft: 2, borderColor: 'primary.main', pl: 1, mb: 2, lineHeight: 1.5 }}>
              활동이 꾸준한지, 어느 날 튀거나 끊겼는지. <b>0인 날은 실제로 결정이 없던 날</b> — 데이터가 근무 패턴을 그대로 반영한다는 뜻(가짜값 아님).
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 100, pt: 1 }}>
              {(data.daily ?? []).map((d) => (
                <Stack key={d.date} sx={{ flex: 1, alignItems: 'center', justifyContent: 'flex-end', height: '100%' }} gap={0.5}>
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }} color="text.secondary">{d.decisions}</Typography>
                  <Box sx={{
                    width: '100%', maxWidth: 34,
                    height: `${Math.max(2, (d.decisions / maxDaily) * 100)}%`,
                    bgcolor: d.decisions === 0 ? 'divider' : 'primary.main',
                    borderRadius: '4px 4px 0 0', minHeight: 2,
                  }} />
                  <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 9.5 }} color="text.secondary">{d.date.slice(5)}</Typography>
                </Stack>
              ))}
            </Box>
          </Paper>

          {/* 성능 · 비용 */}
          <Grid container spacing={2}>
            <Ftile title="매처 소요" value={data.perf?.avg_matcher_ms != null ? `${data.perf.avg_matcher_ms}` : '—'} unit="ms" why="키워드 매칭 자체 시간. 사용자 입력을 지연시키지 않는지 감시." />
            <Ftile title="Hook 총 소요" value={data.perf?.avg_hook_ms != null ? `${data.perf.avg_hook_ms}` : '—'} unit="ms" why="프롬프트 제출 시 붙는 전체 오버헤드. 예산 관리 지표." />
            <Ftile title="출력 토큰 / 7d" value={num(data.agent?.out_tokens)} why="누적 비용 근거. 지금은 free tier — 모델 라우팅 절감의 baseline." />
          </Grid>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, pt: 2, borderTop: 1, borderColor: 'divider', lineHeight: 1.7 }}>
            <b>이 화면이 답하는 질문</b> — ① 파이프라인이 살아있나 · ② 조직이 표준을 실제로 쓰나 · ③ 하네스가 개선할 여지가 있나(밴드 분포) · ④ 느리거나 비싸지 않나.
            <br />
            <b>정확 계측(🤖·🧮) 커버리지</b> — <code>agent_exec</code>/<code>agent_activity_attributed</code>/<code>session_stats</code> 는 SessionEnd transcript-analyzer 가 도는 <b>설치 PC 기준</b>으로만 누적됩니다.
            {' '}수치가 적으면 버그가 아니라 커버리지(팀 전체는 <code>/rederi-update</code> 재벤더링 후 누적). raw 프롬프트·본문은 저장하지 않고 집계 수치만 보관합니다.
            <br />
            <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              source: D1 rederi-telemetry · agent_calls + harness_decisions + agent_exec + agent_activity_attributed + session_stats · {data.window?.from?.slice(0, 10)} ~ {data.window?.to?.slice(0, 10)}
            </span>
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

function Kpi({ title, value, unit, why, accent }) {
  return (
    <Grid item xs={6} md={3}>
      <Paper variant="outlined" sx={{ p: 1.75, height: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 24, fontWeight: 600, lineHeight: 1.2, my: 0.25, color: accent ? `${accent}.main` : 'text.primary' }}>
          {value} {unit && <Typography component="span" sx={{ fontSize: 13 }} color="text.secondary">{unit}</Typography>}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>{why}</Typography>
      </Paper>
    </Grid>
  );
}

// 세션 볼륨 카드 내부의 소형 스탯 타일 (2열 그리드).
function SvTile({ label, value, title, accent }) {
  const tile = (
    <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: 'action.hover', border: 1, borderColor: 'divider', height: '100%' }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 600, display: 'block' }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 20, fontWeight: 600, lineHeight: 1.2, mt: 0.25, color: accent || 'text.primary' }}>{value}</Typography>
    </Box>
  );
  return (
    <Grid item xs={6}>
      {title ? <Tooltip title={title} arrow>{tile}</Tooltip> : tile}
    </Grid>
  );
}

function Ftile({ title, value, unit, why }) {
  return (
    <Grid item xs={12} md={4}>
      <Paper variant="outlined" sx={{ p: 1.75, height: '100%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 20, fontWeight: 600, mt: 0.5 }}>
          {value} {unit && <Typography component="span" sx={{ fontSize: 12 }} color="text.secondary">{unit}</Typography>}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4, mt: 0.5 }}>{why}</Typography>
      </Paper>
    </Grid>
  );
}
